import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { Grid, MenuItem } from "@mui/material";
import TextField from "../common/TextField";
import Button from "../common/Button";
import ConfirmDialoag from "../common/ConfirmDialoag";
import Loader from "../../Layout/Loader";
import Table from "../common/tableWithBackEndPagination";
import { checkPrivileges, PaginationConfig } from "../../Helpers";
import CreateUpdateForm from "./CreateUpdateForm";
import ConvertToManualInvoiceModal from "./ConvertToManualInvoiceModal";
import { deleteQuote, getQuote, listQuotes, updateQuoteStatus } from "./quotesApi";
import {
  QUOTE_STATUS,
  QUOTE_STATUS_FILTER_OPTIONS,
  getQuoteStatusActor,
  isQuoteManualInvoiceCreated,
  quoteToManualInvoiceForm,
  resolveQuoteStatus,
} from "./quoteUtils";

const canDo = (keys) => keys.some((key) => checkPrivileges(key));

const rowDefinitionR = (translate) => [
  { id: "quoteNumber", numeric: false, disablePadding: false, label: translate("quoteNumber") },
  { id: "quoteRefNo", numeric: false, disablePadding: false, label: translate("quoteRefNumber") },
  { id: "quoteDate", numeric: false, disablePadding: false, label: translate("quoteDate") },
  { id: "accountName", numeric: false, disablePadding: false, label: translate("accountName") },
  { id: "plate", numeric: false, disablePadding: false, label: translate("plateNumber") },
  { id: "expiryDate", numeric: false, disablePadding: false, label: translate("quotationExpiry") },
  { id: "salesPersonName", numeric: false, disablePadding: false, label: translate("salesManager") },
  { id: "saleNetAmount", numeric: false, disablePadding: false, label: translate("grandTotal") },
  { id: "status", numeric: false, disablePadding: false, label: translate("status") },
  { id: "statusBy", numeric: false, disablePadding: false, label: translate("statusBy") },
];

export const QuotationsComp = (props) => {
  const logInUser = useSelector((state) => state.logInUsers);
  const themecolors = useSelector((state) => state.themecolors);
  const ServerSetting = useSelector((state) => state.ServerSetting);
  const emptyFilters = {
    accountName: "",
    customerName: "",
    quoteRefNo: "",
    quoteNo: "",
    status: "",
  };
  const [mode, setMode] = useState("list");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [convertInvoiceForm, setConvertInvoiceForm] = useState(null);
  const [state, setState] = useState({
    ...PaginationConfig,
    tableData: [],
    pagData: null,
    loading: false,
  });

  const canCreate = canDo([
    "quotationCreate",
    "quotation",
    "manualinvoiceCreate",
    "invoice",
  ]);
  const canDelete = canDo([
    "quotationDelete",
    "quotation",
    "manualinvoiceDelete",
  ]);
  const canConvertToManualInvoice = checkPrivileges("manualinvoiceCreate");

  const formatStatusBy = (row) => {
    const status = resolveQuoteStatus(row);
    const actor = getQuoteStatusActor(row);
    if (!actor) return "-";
    if (status === QUOTE_STATUS.APPROVED) {
      return `${props.translate("approvedBy")}: ${actor}`;
    }
    if (status === QUOTE_STATUS.REJECTED) {
      return `${props.translate("rejectedBy")}: ${actor}`;
    }
    return "-";
  };

  const loadQuotes = async (activeFilters = appliedFilters, page = state.page, pageSize = state.pageSize) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const result = await listQuotes({
        page,
        pageSize,
        status: activeFilters.status,
        accountName: activeFilters.accountName,
        customerName: activeFilters.customerName,
        quoteRefNo: activeFilters.quoteRefNo,
        quoteNo: activeFilters.quoteNo,
      });
      const tableData = (result.data || []).map((row) => {
        const rawStatus = resolveQuoteStatus(row);
        return {
          ...row,
          quoteDate: row.quoteDate ? moment(row.quoteDate).format("YYYY-MM-DD") : "",
          expiryDate: row.expiryDate ? moment(row.expiryDate).format("YYYY-MM-DD") : "",
          accountName: row.accountName || "-",
          contactPerson: row.contactPerson || "-",
          plate: row.plate || "-",
          salesPersonName: row.salesPersonName || "-",
          grandTotal: (Number(row.grandTotal) || 0).toFixed(2),
          status: props.translate(rawStatus) || rawStatus,
          _rawStatus: rawStatus,
          statusBy: formatStatusBy(row),
        };
      });
      setState((prev) => ({
        ...prev,
        tableData,
        pagData: {
          total: result.total || 0,
          page: result.page || page,
          hasNext: (result.page || page) * pageSize < (result.total || 0),
        },
        loading: false,
      }));
    } catch (error) {
      toast.error(props.translate("somethingWentWrong"));
      setState((prev) => ({
        ...prev,
        loading: false,
        tableData: [],
        pagData: null,
      }));
    }
  };

  useEffect(() => {
    if (logInUser?.id && mode === "list") {
      loadQuotes(appliedFilters, state.page, state.pageSize);
    }
  }, [logInUser?.id, mode, state.page, state.pageSize, appliedFilters]);

  const handleFilterChange = (name) => (e) => {
    setFilters((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const applySearch = () => {
    setAppliedFilters({ ...filters });
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters({ ...emptyFilters });
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applySearch();
    }
  };

  const handleChangeRowsPerPage = (pageSize) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  const handleChangePage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const openCreate = () => {
    setSelectedQuote(null);
    setMode("create");
  };

  const pickRow = (selected) => (Array.isArray(selected) ? selected[0] : selected);

  const isQuoteEditLocked = (quote) => {
    const status = quote?._rawStatus || resolveQuoteStatus(quote);
    return (
      status === QUOTE_STATUS.APPROVED || status === QUOTE_STATUS.REJECTED
    );
  };

  const openEdit = async (selected) => {
    const quote = pickRow(selected);
    // Approved / rejected quotes are view-only — no edit
    if (isQuoteEditLocked(quote)) {
      await openQuote(selected, "view");
      return;
    }
    await openQuote(selected, "edit");
  };

  const openQuote = async (selected, nextMode = "edit") => {
    const quote = pickRow(selected);
    if (!quote?.id) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const full = await getQuote(quote.id);
      const resolved = full || quote;
      const lockedMode = isQuoteEditLocked(resolved) ? "view" : nextMode;
      setSelectedQuote(resolved);
      setMode(lockedMode);
    } catch (error) {
      toast.error(props.translate("somethingWentWrong"));
      setSelectedQuote(quote);
      setMode(isQuoteEditLocked(quote) ? "view" : nextMode);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const openPreview = (selected) => {
    const quote = pickRow(selected);
    if (!quote?.id) return;
    const timezone = logInUser?.attributes?.timezone || "Asia/Riyadh";
    window.open(
      `/quotation-detail?&token=${quote.id}&timezone=${timezone}`,
      "_blank",
    );
  };

  const openConvertModal = (formData) => {
    if (!formData) return;
    setConvertInvoiceForm(formData);
  };

  const convertToManualInvoice = async (selected) => {
    if (!canConvertToManualInvoice) {
      toast.error(props.translate("somethingWentWrong"));
      return;
    }
    const quote = pickRow(selected);
    if (!quote?.id) return;
    const status = quote._rawStatus || resolveQuoteStatus(quote);
    if (status !== QUOTE_STATUS.APPROVED) {
      toast.error(props.translate("onlyApprovedQuoteToInvoice"));
      return;
    }
    if (isQuoteManualInvoiceCreated(quote)) {
      toast.error(props.translate("quoteManualInvoiceAlreadyCreated"));
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const full = await getQuote(quote.id);
      if (isQuoteManualInvoiceCreated(full || quote)) {
        toast.error(props.translate("quoteManualInvoiceAlreadyCreated"));
        return;
      }
      openConvertModal(quoteToManualInvoiceForm(full || quote));
    } catch (error) {
      toast.error(props.translate("somethingWentWrong"));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteQuote(deleteTarget.id);
      toast.success(props.translate("deleteSuccessfully"));
      setDeleteTarget(null);
      loadQuotes();
    } catch (error) {
      toast.error(props.translate("somethingWentWrong"));
    }
  };

  const handleQuoteStatusChange = (row, nextStatus) => {
    if (!row?.id) return;
    if (
      nextStatus !== QUOTE_STATUS.APPROVED &&
      nextStatus !== QUOTE_STATUS.REJECTED
    ) {
      return;
    }
    const current = row._rawStatus || resolveQuoteStatus(row);
    if (
      current === QUOTE_STATUS.APPROVED ||
      current === QUOTE_STATUS.REJECTED ||
      current === QUOTE_STATUS.EXPIRED
    ) {
      toast.error(props.translate("quotationStatusAlreadySet"));
      return;
    }
    setStatusTarget({ quote: row, nextStatus });
  };

  const confirmStatusChange = async () => {
    if (!statusTarget?.quote?.id) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      await updateQuoteStatus(
        statusTarget.quote.id,
        statusTarget.nextStatus,
        logInUser,
      );
      toast.success(
        statusTarget.nextStatus === QUOTE_STATUS.APPROVED
          ? props.translate("quotationApproved")
          : props.translate("quotationRejected"),
      );
      setStatusTarget(null);
      await loadQuotes();
    } catch (error) {
      toast.error(props.translate("somethingWentWrong"));
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const setPagination = (data) => {
    if (!data) return {};
    const lastPage = Math.max(1, Math.ceil((data.total || 0) / state.pageSize));
    return {
      ...data,
      page: data.page || state.page,
      pageSize: state.pageSize,
      lastPage,
      hasNext: (data.page || state.page) < lastPage,
    };
  };

  const goToList = () => {
    setMode("list");
    setSelectedQuote(null);
  };

  const headerTitle =
    mode === "create"
      ? props.translate("createQuotation")
      : mode === "edit"
        ? `${props.translate("editQuotation") || props.translate("sharedEdit")} ${
            selectedQuote?.quoteRefNo || selectedQuote?.quoteNumber || ""
          }`.trim()
        : props.translate("quotations");

  return (
    <div>
      <Grid container className="breadcrumb-row" alignItems="center">
        <Grid item xs={12} sm={mode === "list" ? 2 : 3}>
          <h3 style={{ margin: 0 }}>{headerTitle}</h3>
        </Grid>
        <Grid
          item
          xs={12}
          sm={mode === "list" ? 10 : 9}
          className={mode === "list" ? "users-list-actions" : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "nowrap",
            gap: 8,
            minWidth: 0,
          }}
        >
          {mode === "list" ? (
            <>
              <div className="users-list-actions__field">
                <TextField
                  fullWidth
                  label={props.translate("accountName")}
                  value={filters.accountName}
                  onChange={handleFilterChange("accountName")}
                  onKeyDown={handleFilterKeyDown}
                />
              </div>
              <div className="users-list-actions__field">
                <TextField
                  fullWidth
                  label={props.translate("customerName")}
                  value={filters.customerName}
                  onChange={handleFilterChange("customerName")}
                  onKeyDown={handleFilterKeyDown}
                />
              </div>
              <div className="users-list-actions__field">
                <TextField
                  fullWidth
                  label={props.translate("quoteRefNo")}
                  value={filters.quoteRefNo}
                  onChange={handleFilterChange("quoteRefNo")}
                  onKeyDown={handleFilterKeyDown}
                />
              </div>
              <div className="users-list-actions__field">
                <TextField
                  fullWidth
                  label={props.translate("quoteNumber")}
                  value={filters.quoteNo}
                  onChange={handleFilterChange("quoteNo")}
                  onKeyDown={handleFilterKeyDown}
                />
              </div>
              <div className="users-list-actions__field" style={{ maxWidth: 140 }}>
                <TextField
                  select
                  fullWidth
                  label={props.translate("status")}
                  value={filters.status}
                  onChange={handleFilterChange("status")}
                >
                  {QUOTE_STATUS_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.id || "all"} value={option.id}>
                      {props.translate(option.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="users-list-actions__btn">
                <Button
                  disabled={state.loading}
                  aria-label="Search"
                  size="small"
                  color="inherit"
                  onClick={applySearch}
                >
                  {props.translate("search")}
                </Button>
              </div>
              <div className="users-list-actions__btn">
                <Button
                  disabled={state.loading}
                  aria-label="Reset"
                  size="small"
                  color="inherit"
                  onClick={resetFilters}
                >
                  {props.translate("reset")}
                </Button>
              </div>
              {canCreate && (
                <div className="users-list-actions__btn">
                  <Button
                    disabled={state.loading}
                    aria-label="Create"
                    size="small"
                    color="inherit"
                    onClick={openCreate}
                    id="createButton"
                  >
                    {props.translate("create")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Button
              aria-label="Back"
              size="small"
              color="inherit"
              onClick={goToList}
              id="createButton"
            >
              {props.translate("sharedBack")}
            </Button>
          )}
        </Grid>
      </Grid>

      {mode !== "list" ? (
        <CreateUpdateForm
          {...props}
          key={selectedQuote?.id || mode || "create"}
          mode={mode}
          selectedQuote={selectedQuote}
          onConvertToManualInvoice={
            canConvertToManualInvoice ? openConvertModal : undefined
          }
          onSaved={() => {
            setMode("list");
            setSelectedQuote(null);
          }}
        />
      ) : state.loading ? (
        <Loader />
      ) : (
        <Table
          rows={state.tableData}
          pagination={setPagination(state.pagData)}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onDelete={(selected) => setDeleteTarget(pickRow(selected))}
          onClick={openEdit}
          onShowPaymentDetails={openPreview}
          hasAccessOfDelete={canDelete}
          rowDefinition={rowDefinitionR(props.translate)}
          themecolors={themecolors}
          ServerSetting={ServerSetting}
          logInUser={logInUser}
          isEditable
          isShowPaymentList
          isCursoPointer
          quotations
          onQuoteStatusChange={handleQuoteStatusChange}
          onConvertToManualInvoice={
            canConvertToManualInvoice ? convertToManualInvoice : undefined
          }
        />
      )}

      {deleteTarget && (
        <ConfirmDialoag
          title={props.translate("areYouWantToDelete")}
          onCancel={() => setDeleteTarget(null)}
          onOk={confirmDelete}
        />
      )}

      {statusTarget && (
        <ConfirmDialoag
          title={
            statusTarget.nextStatus === QUOTE_STATUS.APPROVED
              ? props.translate("confirmApproveQuote")
              : props.translate("confirmRejectQuote")
          }
          onCancel={() => setStatusTarget(null)}
          onOk={confirmStatusChange}
        />
      )}

      {convertInvoiceForm && (
        <ConvertToManualInvoiceModal
          {...props}
          key={
            convertInvoiceForm.quoteId ||
            convertInvoiceForm.refNum ||
            "convert"
          }
          themecolors={themecolors}
          initialForm={convertInvoiceForm}
          onClose={() => setConvertInvoiceForm(null)}
          onSuccess={(quoteId) => {
            loadQuotes();
            if (
              quoteId &&
              selectedQuote &&
              String(selectedQuote.id) === String(quoteId)
            ) {
              setSelectedQuote((prev) =>
                prev
                  ? {
                      ...prev,
                      attributes: {
                        ...(prev.attributes || {}),
                        mannualInvoice: true,
                      },
                    }
                  : prev,
              );
            }
          }}
        />
      )}
    </div>
  );
};
